import {
    Component,
    Input,
    Output,
    EventEmitter,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Agent, Protocol } from "../../data/model";
import { KeycloakService } from "../../security";
import { map, Subject } from "rxjs";
import { PersistenceService } from "../../store/model/persistence.service";
import { NotificationService } from "../../services/notification.service";

@Component({
    selector: "pcm-modal",
    templateUrl: "pcm-modal.component.html",
    styleUrls: [
        "zurich-style.css",
        "zurich.css",
        "main.css",
        "pcm-modal.component.css",
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PcmModal {
    @Input() agent: Agent;
    @Input() protocol: Protocol;

    protected serviceUrl = config.PCMSERVICEURL;

    producerCode = "";
    isVisible = false;
    loading = false;
    records$ = new Subject<Agent[]>();
    toggledRecord: number | null = null;

    constructor(
        private http: HttpClient,
        private keycloak: KeycloakService,
        private persistence: PersistenceService,
        private notify: NotificationService,
        private cdr: ChangeDetectorRef,
    ) {}

    public open(): void {
        this.isVisible = true;
        this.load();
    }

    public close(): void {
        if (this.isVisible) {
            this.isVisible = false;
            this.producerCode = "";
            this.records$.next([]);
            this.toggledRecord = null;
        }
    }

    public select(record): void {
        this.updateAgent(record);
        this.close();
    }

    public toggle(index: number): void {
        if (this.toggledRecord === index) {
            this.toggledRecord = null;
        } else {
            this.toggledRecord = index;
        }
    }

    public load() {
        this.toggledRecord = null;
        const token = this.keycloak.getExistingParsedToken();
        // FEGI-4881 only allow SimId filter when user has Role App_ZAS_AD
        const hasAdRole = !!token.realm_access.roles.find(
            (role) => role === "App_ZAS_AD",
        );
        if (hasAdRole && token.buid && this.producerCode.length === 0) {
            this.getAgents(`(SimId%20eq%20%27${token.buid}%27)`);
        } else if (this.producerCode.length > 2) {
            if (hasAdRole && token.buid) {
                this.getAgents(
                    `(SimId%20eq%20%27${token.buid}%27)%20and%20(startswith(ProducerCode,%27${this.producerCode}%27))&$top=20`,
                );
            } else {
                this.getAgents(
                    `startswith(ProducerCode,%27${this.producerCode}%27)&$top=20`,
                );
            }
        } else {
            this.records$.next([]);
        }
    }

    protected getAgents(filter: string) {
        const url = `${this.serviceUrl}/Agents?$filter=${filter}`;
        interface PcmResult {
            value: Agent[];
        }
        this.loading = true;
        this.cdr.detectChanges();
        this.http
            .get<PcmResult>(url)
            .pipe(map((result) => result.value))
            .subscribe(
                (result) => {
                    this.loading = false;
                    this.records$.next(result ? result : []);
                },
                (error) => {
                    this.loading = false;
                    this.notify.errorExt(
                        "Vermittlerdaten abrufen",
                        "Die Vermittlerdaten konnten nicht abgerufen werden.",
                        error,
                    );
                },
            );
    }

    protected updateAgent(record: any): void {
        if (!this.agent) {
            return;
        }
        const agent = this.agent;
        const address = this.agent.Address;

        this.persistence.updateAgent(agent, "ExternalId", record.ProducerCode);
        this.persistence.updateAgent(
            agent,
            "AgencyNumber",
            record.AgencyNumber,
        );
        this.persistence.updateAgent(agent, "FirstName", record.FirstName);
        this.persistence.updateAgent(agent, "LastName", record.LastName, true);
        if (!this.protocol.CreatorName && record.FirstName && record.LastName) {
            this.persistence.updateProtocol(
                this.protocol,
                "CreatorName",
                `${record.FirstName} ${record.LastName}`,
            );
        }

        this.persistence.updateAddress(address, "Street", record.Street);
        this.persistence.updateAddress(address, "Zip", record.ZIP);
        this.persistence.updateAddress(address, "City", record.City);
        this.persistence.updateAddress(address, "PhoneBusiness", record.Phone);
        this.persistence.updateAddress(address, "Telefax", record.Fax);
        this.persistence.updateAddress(address, "EMail", record.Email, true);
    }
}
